<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import ConversationApi from 'dashboard/api/inbox/conversation';

const props = defineProps({
  card: { type: Object, required: true },
  attrConfig: { type: Object, required: true },
  attrDef: { type: Object, default: null },
});
const emit = defineEmits(['updated']);

const { t } = useI18n();

const isEditing = ref(false);
const editValue = ref('');
const isSaving = ref(false);

const displayType = computed(() => props.attrDef?.attribute_display_type || 'text');
const attrValues = computed(() => props.attrDef?.attribute_values || []);

const currentValue = computed(() => {
  const key = props.attrConfig.attribute_key;
  const model = props.attrConfig.attribute_model;
  if (model === 'contact_attribute') {
    if (props.card.item_type === 'Conversation') {
      return props.card.item?.contact_custom_attributes?.[key] ?? '';
    }
    return props.card.item?.custom_attributes?.[key] ?? '';
  }
  // conversation_attribute
  if (props.card.item_type === 'Conversation') {
    return props.card.item?.custom_attributes?.[key] ?? '';
  }
  // Contact-type card: read from the most recent conversation's attributes
  return props.card.item?.conversation_custom_attributes?.[key] ?? '';
});

const displayValue = computed(() => {
  const val = currentValue.value;
  if (displayType.value === 'checkbox') return val ? '✓' : '—';
  if (val === '' || val === null || val === undefined) return '';
  return String(val);
});

const startEdit = () => {
  if (displayType.value === 'checkbox') {
    toggleCheckbox();
    return;
  }
  editValue.value = String(currentValue.value ?? '');
  isEditing.value = true;
};

const cancelEdit = () => {
  isEditing.value = false;
};

const toggleCheckbox = async () => {
  if (isSaving.value) return;
  const newVal = !currentValue.value;
  isSaving.value = true;
  try {
    await saveValue(newVal);
    emit('updated', newVal);
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.ATTR_UPDATE'));
  } finally {
    isSaving.value = false;
  }
};

const saveEdit = async () => {
  if (isSaving.value) return;
  isEditing.value = false;

  let newVal = editValue.value;
  if (['number', 'currency', 'percent'].includes(displayType.value) && newVal !== '') {
    newVal = Number(newVal);
  }

  if (newVal === currentValue.value || (newVal === '' && (currentValue.value === '' || currentValue.value === null || currentValue.value === undefined))) return;

  isSaving.value = true;
  try {
    await saveValue(newVal);
    emit('updated', newVal);
  } catch {
    useAlert(t('KANBAN_VIEW.ERRORS.ATTR_UPDATE'));
  } finally {
    isSaving.value = false;
  }
};

const saveValue = async (val) => {
  const key = props.attrConfig.attribute_key;
  const model = props.attrConfig.attribute_model;

  if (model === 'contact_attribute') {
    const contactId = props.card.item_type === 'Conversation'
      ? props.card.item?.contact_id
      : props.card.item?.id;
    if (contactId) {
      // Merge to avoid losing other contact custom attributes
      const existing = props.card.item_type === 'Conversation'
        ? (props.card.item?.contact_custom_attributes || {})
        : (props.card.item?.custom_attributes || {});
      await ContactAPI.update(contactId, { custom_attributes: { ...existing, [key]: val } });
    }
  } else if (model === 'conversation_attribute') {
    const conversationDisplayId = props.card.item_type === 'Conversation'
      ? props.card.item?.display_id
      : props.card.item?.recent_conversation_display_id;
    if (conversationDisplayId) {
      // Merge to avoid losing other conversation custom attributes
      const existing = props.card.item_type === 'Conversation'
        ? (props.card.item?.custom_attributes || {})
        : (props.card.item?.conversation_custom_attributes || {});
      await ConversationApi.updateCustomAttributes({
        conversationId: conversationDisplayId,
        customAttributes: { ...existing, [key]: val },
      });
    }
  }
};
</script>

<template>
  <div @click.stop>
    <!-- list/select type -->
    <select
      v-if="displayType === 'list' && isEditing"
      v-model="editValue"
      class="w-32 rounded border border-n-alpha-4 bg-n-solid-1 px-1.5 py-0.5 text-right text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
      @change="saveEdit"
      @blur="cancelEdit"
      @keyup.escape="cancelEdit"
    >
      <option value="">—</option>
      <option v-for="opt in attrValues" :key="opt" :value="opt">{{ opt }}</option>
    </select>

    <!-- number/currency/percent type -->
    <input
      v-else-if="['number', 'currency', 'percent'].includes(displayType) && isEditing"
      v-model="editValue"
      type="number"
      class="w-32 rounded border border-n-alpha-4 bg-n-solid-1 px-1.5 py-0.5 text-right text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
      @blur="saveEdit"
      @keyup.enter="saveEdit"
      @keyup.escape="cancelEdit"
    />

    <!-- date type -->
    <input
      v-else-if="displayType === 'date' && isEditing"
      v-model="editValue"
      type="date"
      class="w-36 rounded border border-n-alpha-4 bg-n-solid-1 px-1.5 py-0.5 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
      @blur="saveEdit"
      @keyup.enter="saveEdit"
      @keyup.escape="cancelEdit"
    />

    <!-- text/link/default type -->
    <input
      v-else-if="isEditing"
      v-model="editValue"
      :type="displayType === 'link' ? 'url' : 'text'"
      class="w-32 rounded border border-n-alpha-4 bg-n-solid-1 px-1.5 py-0.5 text-right text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-blue-7"
      @blur="saveEdit"
      @keyup.enter="saveEdit"
      @keyup.escape="cancelEdit"
    />

    <!-- checkbox toggle (no edit mode) -->
    <button
      v-else-if="displayType === 'checkbox'"
      type="button"
      :disabled="isSaving"
      class="flex items-center justify-end text-xs font-medium"
      :class="currentValue ? 'text-n-teal-11' : 'text-n-slate-8'"
      @click="toggleCheckbox"
    >
      <span :class="currentValue ? 'i-lucide-check-square' : 'i-lucide-square'" class="size-3.5" />
    </button>

    <!-- display button for all other types -->
    <button
      v-else
      type="button"
      class="max-w-[128px] truncate text-right text-xs font-medium hover:text-n-brand"
      :class="displayValue ? 'text-n-slate-12' : 'text-n-slate-8 italic'"
      :title="displayValue"
      @click="startEdit"
    >
      {{ displayValue || t('KANBAN_VIEW.STAGE_CONFIG.ATTR_EMPTY') }}
    </button>
  </div>
</template>
